import { Shell } from '@/components/Shell';
import { EmptyBlock } from '@/components/ui/EmptyState';
import { adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/reviews')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: ReviewsPage,
});

const STATES = [
  { v: 'pending', l: 'PENDENTES' },
  { v: '', l: 'TODAS' },
  { v: 'approved', l: 'APROVADAS' },
  { v: 'rejected', l: 'REJEITADAS' },
];

function ReviewsPage() {
  const qc = useQueryClient();
  const [state, setState] = useState('pending');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'reviews', state],
    queryFn: () => adminApi.reviews({ state: state || undefined }),
  });

  const moderate = useMutation({
    mutationFn: (args: { id: string; state: 'approved' | 'rejected' }) =>
      adminApi.moderateReview(args.id, args.state),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'reviews'] }),
  });

  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short' });
  const count = data?.paging.count ?? 0;

  return (
    <Shell page="AVALIAÇÕES" sub={`MODERAÇÃO · ${count} REGISTOS`}>
      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {STATES.map((s) => (
          <button
            key={s.v || 'all'}
            type="button"
            onClick={() => setState(s.v)}
            className={`chip${state === s.v ? ' active' : ''}`}
          >
            {s.l}
          </button>
        ))}
      </div>

      {/* Review cards */}
      {isLoading ? (
        <div style={{ padding: 24, color: 'var(--lv-gray-500)' }}>A carregar…</div>
      ) : data?.items.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid var(--lv-line)' }}>
          <EmptyBlock message="Sem avaliações para este filtro." />
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
          {data?.items.map((r) => (
            <li
              key={r.id}
              style={{
                background: '#fff',
                border: '1px solid var(--lv-line)',
                padding: 0,
                overflow: 'hidden',
              }}
            >
              {/* State accent strip */}
              <div
                style={{
                  height: 3,
                  background:
                    r.state === 'approved'
                      ? 'var(--lv-ok)'
                      : r.state === 'rejected'
                        ? 'var(--lv-err)'
                        : 'var(--lv-warn)',
                }}
              />

              <div style={{ padding: 20 }}>
                {/* Header row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.authorName}</div>
                    <div style={{ fontSize: 11, color: 'var(--lv-gray-500)', marginTop: 2 }}>
                      <span
                        style={{
                          fontFamily: 'var(--lv-font-mono)',
                          fontWeight: 700,
                          color: 'var(--lv-brand)',
                        }}
                      >
                        {r.bookingCode}
                      </span>
                      {' · '}
                      {r.vehicleName}
                      {' · '}
                      {dateFmt.format(new Date(r.createdAt))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Star rating */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 2,
                        fontSize: 18,
                        color: 'var(--lv-amber)',
                        lineHeight: 1,
                      }}
                    >
                      {'★'.repeat(r.stars)}
                      <span style={{ color: 'var(--lv-line)' }}>{'★'.repeat(Math.max(0, 5 - r.stars))}</span>
                    </div>

                    {/* State indicator */}
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color:
                          r.state === 'approved'
                            ? 'var(--lv-ok)'
                            : r.state === 'rejected'
                              ? 'var(--lv-err)'
                              : 'var(--lv-warn)',
                      }}
                    >
                      ●{' '}
                      {r.state === 'approved'
                        ? 'APROVADA'
                        : r.state === 'rejected'
                          ? 'REJEITADA'
                          : 'PENDENTE'}
                    </span>
                  </div>
                </div>

                {/* Review text */}
                <p
                  style={{
                    margin: '10px 0',
                    fontSize: 14,
                    lineHeight: 1.55,
                    color: 'var(--lv-ink)',
                    fontStyle: 'italic',
                    padding: '10px 14px',
                    background: 'var(--lv-surface-soft)',
                    borderLeft: '3px solid var(--lv-line)',
                  }}
                >
                  "{r.preview}"
                </p>

                {/* Actions */}
                {r.state === 'pending' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => moderate.mutate({ id: r.id, state: 'rejected' })}
                      disabled={moderate.isPending}
                      style={btnDanger}
                    >
                      Rejeitar
                    </button>
                    <button
                      type="button"
                      onClick={() => moderate.mutate({ id: r.id, state: 'approved' })}
                      disabled={moderate.isPending}
                      style={btnPrimary}
                    >
                      Aprovar
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--lv-brand)',
  color: '#fff',
  border: 0,
  padding: '9px 18px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  background: '#fff',
  color: 'var(--lv-err)',
  border: '1px solid var(--lv-err)',
  padding: '9px 18px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
